import { getAdminClient } from '@beatq/sdk-client';
import { mapErrorCode, readDataFromEnvelope, readListFromEnvelope } from '@beatq/shared';
import type { MirrorSubjectKind } from './catalog.js';
import { defaultImpersonationExpiryIso } from './entity360Rules.js';

type ApiErr = { error?: { code: string; message: string } };

export type Customer360Profile = {
  id?: string;
  accountId?: string | null;
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  mobile?: string | null;
  status?: string | null;
  createdAt?: string | null;
  publicReference?: string | null;
  marketingConsent?: boolean;
  contactRevealed?: boolean;
  addressRevealed?: boolean;
  addresses?: Array<{
    id?: string;
    label?: string | null;
    line1?: string | null;
    line2?: string | null;
    city?: string | null;
    postcode?: string | null;
    postcodeDistrict?: string | null;
  }>;
};

export type Merchant360Profile = {
  id?: string;
  tradingName?: string | null;
  legalName?: string | null;
  status?: string | null;
  verticalCode?: string | null;
  primaryVertical?: string | null;
  billingEmail?: string | null;
  verificationState?: string | null;
  publicReference?: string | null;
  gmvBand?: string | null;
  orderCount?: number | null;
  openDisputeCount?: number | null;
  riskFlag?: string | null;
};

export type BranchSummary = {
  id?: string;
  name?: string | null;
  status?: string | null;
};

export type StaffSummary = {
  id?: string;
  name?: string | null;
  role?: string | null;
  status?: string | null;
};

export type OrderSummary = {
  id?: string;
  publicReference?: string | null;
  status?: string | null;
  totalMinor?: number | null;
  createdAt?: string | null;
};

export type CaseSummary = {
  id?: string;
  publicReference?: string | null;
  subject?: string | null;
  status?: string | null;
  state?: string | null;
};

export type RemedySummary = {
  id?: string;
  type?: string | null;
  status?: string | null;
  reason?: string | null;
  amountMinor?: number | null;
  createdAt?: string | null;
};

export type WalletSummary = {
  balanceMinor?: number | null;
  availableMinor?: number | null;
  currency?: string | null;
  state?: string | null;
  expiresAt?: string | null;
  expiringSoon?: Array<{ amountMinor: number; currency: string; expiresAt: string }>;
};

export type FlagSummary = {
  id?: string;
  label?: string;
  value?: string | null;
};

export type MirrorPanel = {
  title?: string;
  body?: string | null;
  readOnly?: boolean;
};

export type ImpersonationSession = {
  id?: string;
  impersonationId?: string;
  subjectId?: string;
  subjectKind?: string;
  scope?: string | null;
  mode?: string | null;
  expiresAt?: string | null;
  expiresIn?: number | null;
  bannerText?: string | null;
  accessToken?: string | null;
  targetSurface?: string | null;
  restrictions?: string[];
};

export type Customer360Load = {
  customer: Customer360Profile | undefined;
  orders: OrderSummary[];
  cases: CaseSummary[];
  remedies: RemedySummary[];
  wallet: WalletSummary | undefined;
  flags: FlagSummary[];
  failedRegions: string[];
};

export type Merchant360Load = {
  business: Merchant360Profile | undefined;
  branches: BranchSummary[];
  staff: StaffSummary[];
  performance: FlagSummary[];
  deductions: FlagSummary[];
  disputes: FlagSummary[];
  verification: FlagSummary[];
  failedRegions: string[];
};

function asRecord(value: unknown): Record<string, unknown> | undefined {
  if (value != null && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return undefined;
}

function asString(value: unknown): string | undefined {
  if (typeof value === 'string' && value.trim()) return value;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return undefined;
}

function asNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() && Number.isFinite(Number(value))) {
    return Number(value);
  }
  return undefined;
}

export function sdkErrorMessage(error: unknown): string {
  const e = error as ApiErr;
  return e?.error?.message ?? mapErrorCode(e?.error?.code ?? 'ERR-UNKNOWN');
}

export function isStepUpRequiredError(error: unknown): boolean {
  if (error == null) return false;
  const e = error as ApiErr & { message?: string; code?: string };
  const code = e?.error?.code ?? e?.code ?? '';
  const message = e?.error?.message ?? e?.message ?? (error instanceof Error ? error.message : '');
  return code === 'ERR-AUTH-0051' || /step-up|extra confirmation|re-authenticate/i.test(message);
}

/** 10-03 ERR-AUTH-0051 body includes `stepUp.challengeToken`. Live Zod for mfa/verify requires it. */
export function extractStepUpChallenge(error: unknown): string | null {
  const visit = (value: unknown, depth = 0): string | null => {
    if (value == null || depth > 6) return null;
    const rec = asRecord(value);
    if (!rec) return null;
    const direct = asString(rec.challengeToken) ?? asString(asRecord(rec.stepUp)?.challengeToken);
    if (direct) return direct;
    for (const key of ['error', 'stepUp', 'details'] as const) {
      const nested = rec[key];
      if (Array.isArray(nested)) {
        for (const item of nested) {
          const found = visit(item, depth + 1);
          if (found) return found;
        }
      } else {
        const found = visit(nested, depth + 1);
        if (found) return found;
      }
    }
    return null;
  };
  if (error instanceof Error) {
    const extra = error as Error & { stepUp?: unknown; error?: unknown };
    return visit(extra) ?? visit(extra.stepUp) ?? visit(extra.error);
  }
  return visit(error);
}

function throwSdkError(error: unknown): never {
  const rec = asRecord(error);
  const nested = asRecord(rec?.error);
  const err = new Error(sdkErrorMessage(error)) as Error & {
    code?: string;
    stepUp?: unknown;
    error?: unknown;
  };
  err.code = asString(nested?.code) ?? asString(rec?.code);
  err.stepUp =
    nested?.stepUp ??
    asRecord(nested?.details)?.stepUp ??
    rec?.stepUp ??
    asRecord(rec?.details)?.stepUp;
  err.error = rec?.error ?? rec;
  throw err;
}

export function impersonationWriteEnabled(
  session: ImpersonationSession | undefined,
  requestedWrite: boolean,
): boolean {
  const mode = (session?.mode ?? session?.scope ?? '').toUpperCase();
  if (mode === 'READ_WRITE' || mode === 'WRITE') return true;
  if (mode === 'READ_ONLY' || mode === 'READ') return false;
  return requestedWrite;
}

function payloadRecord(envelope: unknown): Record<string, unknown> | undefined {
  return asRecord(readDataFromEnvelope(envelope)) ?? asRecord(envelope);
}

export function customerFromEnvelope(envelope: unknown): Customer360Profile | undefined {
  const data = payloadRecord(envelope);
  if (!data) return undefined;
  const row =
    asRecord(data.customer) ?? (data.id || data.email || data.firstName ? data : undefined);
  if (!row) return undefined;
  return {
    id: asString(row.id),
    accountId: asString(row.accountId) ?? null,
    email: asString(row.email) ?? null,
    firstName: asString(row.firstName) ?? null,
    lastName: asString(row.lastName) ?? null,
    mobile: asString(row.mobile) ?? asString(row.phone) ?? asString(row.contactPhoneE164) ?? null,
    status: asString(row.status) ?? null,
    createdAt: asString(row.createdAt) ?? null,
    publicReference: asString(row.publicReference) ?? null,
    marketingConsent: typeof row.marketingConsent === 'boolean' ? row.marketingConsent : undefined,
    contactRevealed: row.contactRevealed === true || row.revealContact === true,
    addressRevealed: row.addressRevealed === true || row.revealAddress === true,
    addresses: Array.isArray(row.addresses)
      ? (row.addresses as Record<string, unknown>[]).map((addr) => ({
          id: asString(addr.id),
          label: asString(addr.label) ?? null,
          line1: asString(addr.line1) ?? null,
          line2: asString(addr.line2) ?? null,
          city: asString(addr.city) ?? null,
          postcode: asString(addr.postcode) ?? null,
          postcodeDistrict:
            asString(addr.postcodeDistrict) ?? asString(addr.postcodeOutward) ?? null,
        }))
      : undefined,
  };
}

export function customerContextFromEnvelope(
  envelope: unknown,
): Record<string, unknown> | undefined {
  const data = payloadRecord(envelope);
  if (!data) return undefined;
  return asRecord(data.context) ?? data;
}

export function businessFromEnvelope(envelope: unknown): Merchant360Profile | undefined {
  const data = payloadRecord(envelope);
  if (!data) return undefined;
  const row =
    asRecord(data.business) ?? (data.id || data.tradingName || data.legalName ? data : undefined);
  if (!row) return undefined;
  return {
    id: asString(row.id),
    tradingName: asString(row.tradingName) ?? null,
    legalName: asString(row.legalName) ?? null,
    status: asString(row.status) ?? null,
    verticalCode: asString(row.verticalCode) ?? asString(row.primaryVertical) ?? null,
    primaryVertical: asString(row.primaryVertical) ?? null,
    billingEmail: asString(row.billingEmail) ?? null,
    verificationState: asString(row.verificationState) ?? null,
    publicReference: asString(row.publicReference) ?? null,
    gmvBand: asString(row.gmvBand) ?? null,
    orderCount: asNumber(row.orderCount) ?? null,
    openDisputeCount: asNumber(row.openDisputeCount) ?? asNumber(row.openDisputes) ?? null,
    riskFlag: asString(row.riskFlag) ?? asString(row.riskState) ?? null,
  };
}

export function businessContextFromEnvelope(
  envelope: unknown,
): Record<string, unknown> | undefined {
  const data = payloadRecord(envelope);
  if (!data) return undefined;
  return asRecord(data.context) ?? data;
}

export function branchesFromEnvelope(envelope: unknown): BranchSummary[] {
  return readListFromEnvelope<Record<string, unknown>>(envelope, ['branches', 'items', 'data']).map(
    (row) => ({
      id: asString(row.id),
      name: asString(row.name) ?? asString(row.tradingName) ?? null,
      status: asString(row.status) ?? null,
    }),
  );
}

export function staffFromEnvelope(envelope: unknown): StaffSummary[] {
  return readListFromEnvelope<Record<string, unknown>>(envelope, ['staff', 'items', 'data']).map(
    (row) => ({
      id: asString(row.id),
      name: asString(row.name) ?? asString(row.displayName) ?? null,
      role: asString(row.role) ?? asString(row.roleCode) ?? null,
      status: asString(row.status) ?? null,
    }),
  );
}

export function ordersFromEnvelope(envelope: unknown): OrderSummary[] {
  return readListFromEnvelope<Record<string, unknown>>(envelope, ['orders', 'items', 'data']).map(
    (row) => ({
      id: asString(row.id),
      publicReference: asString(row.publicReference) ?? asString(row.reference) ?? null,
      status: asString(row.status) ?? null,
      totalMinor: asNumber(row.totalMinor) ?? asNumber(row.grandTotalMinor) ?? null,
      createdAt: asString(row.createdAt) ?? asString(row.placedAt) ?? null,
    }),
  );
}

export function casesFromContext(context: Record<string, unknown> | undefined): CaseSummary[] {
  if (!context) return [];
  return readListFromEnvelope<Record<string, unknown>>({ data: context }, [
    'openCases',
    'cases',
    'items',
  ]).map((row) => ({
    id: asString(row.id),
    publicReference: asString(row.publicReference) ?? asString(row.reference) ?? null,
    subject: asString(row.subject) ?? asString(row.reasonCode) ?? null,
    status: asString(row.status) ?? asString(row.state) ?? null,
    state: asString(row.state) ?? null,
  }));
}

export function remediesFromContext(context: Record<string, unknown> | undefined): RemedySummary[] {
  if (!context) return [];
  return readListFromEnvelope<Record<string, unknown>>({ data: context }, [
    'remedies',
    'items',
  ]).map((row) => ({
    id: asString(row.id),
    type: asString(row.type) ?? asString(row.actionCode) ?? null,
    status: asString(row.status) ?? asString(row.state) ?? null,
    reason: asString(row.reason) ?? asString(row.reasonCode) ?? null,
    amountMinor: asNumber(row.amountMinor) ?? null,
    createdAt: asString(row.createdAt) ?? null,
  }));
}

function expiringSoonFrom(wallet: Record<string, unknown>): Array<{
  amountMinor: number;
  currency: string;
  expiresAt: string;
}> {
  if (!Array.isArray(wallet.expiringSoon)) return [];
  return (wallet.expiringSoon as Record<string, unknown>[])
    .map((row) => {
      const amount = asRecord(row.amount);
      const amountMinor =
        asNumber(row.amountMinor) ?? asNumber(amount?.minor) ?? asNumber(row.remainingMinor);
      const expiresAt = asString(row.expiresAt);
      if (amountMinor == null || !expiresAt) return null;
      return {
        amountMinor,
        currency: asString(row.currency) ?? asString(amount?.currency) ?? 'GBP',
        expiresAt,
      };
    })
    .filter((row): row is { amountMinor: number; currency: string; expiresAt: string } =>
      Boolean(row),
    );
}

export function walletFromContext(
  context: Record<string, unknown> | undefined,
): WalletSummary | undefined {
  if (!context) return undefined;
  const wallet = asRecord(context.wallet) ?? asRecord(context.loyalty);
  if (!wallet) {
    const balance = asNumber(context.walletBalanceMinor) ?? asNumber(context.balanceMinor);
    if (balance == null) return undefined;
    return {
      balanceMinor: balance,
      availableMinor: asNumber(context.availableMinor) ?? balance,
      currency: asString(context.currency) ?? 'GBP',
      expiresAt: asString(context.expiresAt) ?? null,
      expiringSoon: [],
    };
  }
  const expiringSoon = expiringSoonFrom(wallet);
  return {
    balanceMinor:
      asNumber(wallet.balanceMinor) ?? asNumber(wallet.availableMinor) ?? asNumber(wallet.balance),
    availableMinor: asNumber(wallet.availableMinor) ?? asNumber(wallet.balanceMinor),
    currency: asString(wallet.currency) ?? 'GBP',
    state: asString(wallet.state) ?? asString(wallet.status) ?? null,
    expiresAt: asString(wallet.expiresAt) ?? expiringSoon[0]?.expiresAt ?? null,
    expiringSoon,
  };
}

export function flagsFromCustomer(
  customer: Customer360Profile | undefined,
  context: Record<string, unknown> | undefined,
): FlagSummary[] {
  const flags: FlagSummary[] = [];
  if (customer?.status) flags.push({ id: 'status', label: 'Account', value: customer.status });
  if (customer?.marketingConsent != null) {
    flags.push({
      id: 'marketing',
      label: 'Marketing consent',
      value: customer.marketingConsent ? 'Yes' : 'No',
    });
  }
  const fromContext = context
    ? readListFromEnvelope<Record<string, unknown>>({ data: context }, ['flags'])
    : [];
  for (const row of fromContext) {
    flags.push({
      id: asString(row.id) ?? asString(row.key) ?? asString(row.label),
      label: asString(row.label) ?? asString(row.key) ?? 'Flag',
      value: asString(row.value) ?? asString(row.status) ?? null,
    });
  }
  return flags;
}

function kvFlags(source: Record<string, unknown> | undefined, keys: string[]): FlagSummary[] {
  if (!source) return [];
  const rows: FlagSummary[] = [];
  for (const key of keys) {
    const value = source[key];
    if (value == null || value === '') continue;
    rows.push({
      id: key,
      label: key.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase()),
      value: typeof value === 'string' ? value : JSON.stringify(value),
    });
  }
  return rows;
}

export function merchantPerformanceFrom(
  business: Merchant360Profile | undefined,
  context: Record<string, unknown> | undefined,
): FlagSummary[] {
  const fromBusiness = kvFlags(business as unknown as Record<string, unknown> | undefined, [
    'gmvBand',
    'orderCount',
    'status',
  ]);
  const ctx = context ? kvFlags(context, ['gmvBand', 'orderCount', 'performance']) : [];
  const liveOrderCount = asNumber(context?.liveOrderCount);
  const live =
    liveOrderCount != null
      ? [{ id: 'liveOrderCount', label: 'Live orders', value: String(liveOrderCount) }]
      : [];
  const nested = context
    ? readListFromEnvelope<Record<string, unknown>>({ data: context }, ['performance', 'metrics'])
    : [];
  return [
    ...fromBusiness,
    ...ctx,
    ...live,
    ...nested.map((row) => ({
      id: asString(row.id) ?? asString(row.key),
      label: asString(row.label) ?? asString(row.key) ?? 'Metric',
      value: asString(row.value) ?? asString(row.status) ?? null,
    })),
  ];
}

function moneyMeta(amountMinor: number | undefined, currency?: string, fallback?: string | null) {
  if (amountMinor == null) return fallback ?? null;
  const code = currency?.trim() || 'GBP';
  try {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: code }).format(
      amountMinor / 100,
    );
  } catch {
    return `${code} ${(amountMinor / 100).toFixed(2)}`;
  }
}

export function merchantDeductionsFrom(
  context: Record<string, unknown> | undefined,
): FlagSummary[] {
  if (!context) return [];
  return readListFromEnvelope<Record<string, unknown>>({ data: context }, [
    'deductions',
    'adjustments',
  ]).map((row) => ({
    id: asString(row.id),
    label: asString(row.label) ?? asString(row.type) ?? 'Deduction',
    value:
      [
        moneyMeta(asNumber(row.amountMinor), asString(row.currency), asString(row.status)),
        asString(row.effectiveAt),
        asString(row.orderId) ? `order ${asString(row.orderId)}` : null,
      ]
        .filter(Boolean)
        .join(' · ') || null,
  }));
}

export function merchantDisputesFrom(
  business: Merchant360Profile | undefined,
  context: Record<string, unknown> | undefined,
): FlagSummary[] {
  const rows: FlagSummary[] = [];
  if (business?.openDisputeCount != null) {
    rows.push({
      id: 'open-disputes',
      label: 'Open disputes',
      value: String(business.openDisputeCount),
    });
  }
  if (!context) return rows;
  return [
    ...rows,
    ...readListFromEnvelope<Record<string, unknown>>({ data: context }, ['disputes']).map(
      (row) => ({
        id: asString(row.id),
        label: asString(row.reason) ?? asString(row.type) ?? 'Dispute',
        value:
          [
            asString(row.status) ?? asString(row.state),
            moneyMeta(asNumber(row.amountMinor), asString(row.currency)),
          ]
            .filter(Boolean)
            .join(' · ') || null,
      }),
    ),
  ];
}

export function merchantVerificationFrom(
  business: Merchant360Profile | undefined,
  context: Record<string, unknown> | undefined,
): FlagSummary[] {
  const rows: FlagSummary[] = [];
  if (business?.verificationState) {
    rows.push({
      id: 'verification-state',
      label: 'Verification',
      value: business.verificationState,
    });
  }
  if (!context) return rows;
  const suspensions = readListFromEnvelope<Record<string, unknown>>({ data: context }, [
    'suspensions',
  ]);
  return [
    ...rows,
    ...suspensions.map((row) => ({
      id: asString(row.id),
      label: 'Active suspension',
      value: asString(row.reasonCode) ?? asString(row.state) ?? 'ACTIVE',
    })),
  ];
}

export function mirrorPanelsFromContext(
  context: Record<string, unknown> | undefined,
): MirrorPanel[] {
  if (!context) return [];
  const named = [
    { key: 'customer', title: 'Customer view' },
    { key: 'business', title: 'Merchant view' },
    { key: 'orders', title: 'Orders as shown' },
    { key: 'openCases', title: 'Open cases' },
    { key: 'remedies', title: 'Remedies' },
    { key: 'branches', title: 'Branches' },
    { key: 'suspensions', title: 'Trading state' },
    { key: 'wallet', title: 'Wallet' },
    { key: 'liveOrderCount', title: 'Live orders' },
    { key: 'performance', title: 'Performance' },
    { key: 'deductions', title: 'Deductions' },
    { key: 'disputes', title: 'Disputes' },
  ];
  const panels: MirrorPanel[] = [];
  const used = new Set<string>();
  for (const item of named) {
    const value = context[item.key];
    if (value == null) continue;
    used.add(item.key);
    panels.push({
      title: item.title,
      body: typeof value === 'string' ? value : JSON.stringify(value),
      readOnly: true,
    });
  }
  if (panels.length > 0) return panels;
  const generic = context.panels ?? context.views ?? context.sections;
  if (Array.isArray(generic)) {
    return (generic as MirrorPanel[]).map((panel) => ({ ...panel, readOnly: true }));
  }
  return Object.entries(context)
    .filter(([key]) => !used.has(key))
    .slice(0, 6)
    .map(([key, value]) => ({
      title: key,
      body: typeof value === 'string' ? value : JSON.stringify(value),
      readOnly: true,
    }));
}

export function impersonationSessionFromEnvelope(
  envelope: unknown,
): ImpersonationSession | undefined {
  const data = payloadRecord(envelope);
  if (!data) return undefined;
  const row = asRecord(data.session) ?? data;
  const id =
    asString(row.id) ??
    asString(row.impersonationId) ??
    asString(row.sessionId) ??
    asString(data.id);
  if (!id && row.expiresIn == null && !row.expiresAt) return undefined;
  const expiresIn = asNumber(row.expiresIn) ?? asNumber(data.expiresIn);
  const expiresAt =
    asString(row.expiresAt) ??
    asString(data.expiresAt) ??
    (expiresIn != null
      ? new Date(Date.now() + expiresIn * 1000).toISOString()
      : defaultImpersonationExpiryIso());
  return {
    id,
    impersonationId: asString(row.impersonationId) ?? id,
    subjectId:
      asString(row.subjectId) ?? asString(row.targetAccountId) ?? asString(row.subjectAccountId),
    subjectKind:
      asString(row.subjectKind) ?? asString(row.targetType) ?? asString(row.targetSurface),
    scope: asString(row.scope) ?? asString(row.mode),
    mode: asString(row.mode),
    expiresAt,
    expiresIn,
    bannerText: asString(row.bannerText),
    accessToken: asString(row.accessToken) ?? asString(data.accessToken),
    targetSurface: asString(row.targetSurface) ?? asString(data.targetSurface),
    restrictions: Array.isArray(row.restrictions)
      ? row.restrictions.filter((item): item is string => typeof item === 'string')
      : Array.isArray(data.restrictions)
        ? data.restrictions.filter((item): item is string => typeof item === 'string')
        : undefined,
  };
}

export function customerDisplayName(customer: Customer360Profile | undefined): string {
  if (!customer) return 'Customer';
  const name = [customer.firstName, customer.lastName].filter(Boolean).join(' ').trim();
  return name || customer.publicReference || customer.id || 'Customer';
}

export function merchantDisplayName(business: Merchant360Profile | undefined): string {
  return business?.tradingName ?? business?.legalName ?? business?.id ?? 'Merchant';
}

function accessHeaders(accessReason?: string): Record<string, string> | undefined {
  const reason = accessReason?.trim();
  return reason ? { 'X-Access-Reason': reason } : undefined;
}

export async function getCustomer(customerId: string, accessReason?: string) {
  const client = getAdminClient();
  const { data, error, response } = await client.GET('/admin/v1/customers/{id}', {
    params: { path: { id: customerId } },
    headers: accessHeaders(accessReason) as never,
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function getCustomerContext(customerId: string, accessReason?: string) {
  const client = getAdminClient();
  const { data, error, response } = await client.GET('/admin/v1/context/customers/{id}', {
    params: { path: { id: customerId } },
    headers: accessHeaders(accessReason) as never,
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function listCustomerOrders(customerId: string) {
  const client = getAdminClient();
  const { data, error, response } = await client.GET('/admin/v1/customers/{id}/orders', {
    params: { path: { id: customerId } },
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function getBusiness(businessId: string) {
  const client = getAdminClient();
  const { data, error, response } = await client.GET('/admin/v1/businesses/{id}', {
    params: { path: { id: businessId } },
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function getBusinessContext(businessId: string) {
  const client = getAdminClient();
  const { data, error, response } = await client.GET('/admin/v1/context/businesses/{id}', {
    params: { path: { id: businessId } },
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function listBusinessBranches(businessId: string) {
  const client = getAdminClient();
  const { data, error, response } = await client.GET('/admin/v1/businesses/{id}/branches', {
    params: { path: { id: businessId } },
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function listBusinessStaff(businessId: string) {
  const client = getAdminClient();
  const { data, error, response } = await client.GET('/admin/v1/businesses/{id}/staff', {
    params: { path: { id: businessId } },
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function verifyImpersonationStepUp(code: string, challengeToken: string) {
  const client = getAdminClient();
  const { data, error, response } = await client.POST('/admin/v1/auth/mfa/verify', {
    body: { code, challengeToken } as never,
  });
  if (!response.ok || error) throwSdkError(error);
  return data;
}

export async function startImpersonationWithStepUp(
  body: {
    subjectAccountId: string;
    supportCaseId: string;
    justification: string;
    consentEvidence?: string;
    emergencyBasis?: string;
    mode?: 'READ_ONLY' | 'READ_WRITE';
    durationMinutes?: number;
  },
  mfaCode?: string,
) {
  try {
    return await startImpersonationSession(body);
  } catch (err) {
    if (!isStepUpRequiredError(err)) throw err;
    const challengeToken = extractStepUpChallenge(err);
    const code = mfaCode?.trim() ?? '';
    if (!challengeToken || !code) {
      const next = new Error(
        'MFA must be fresh within 5 minutes. Enter your authentication code, then start again.',
      ) as Error & { code?: string; needsMfa?: boolean; stepUp?: unknown };
      next.code = 'ERR-AUTH-0051';
      next.needsMfa = true;
      next.stepUp = challengeToken ? { challengeToken } : extractStepUpChallenge(err);
      throw next;
    }
    await verifyImpersonationStepUp(code, challengeToken);
    return startImpersonationSession(body);
  }
}

export async function loadCustomer360(
  customerId: string,
  accessReason: string,
): Promise<Customer360Load> {
  const reason = accessReason.trim();
  const failedRegions: string[] = [];
  const customerResult = await getCustomer(customerId, reason);
  const customer = customerFromEnvelope(customerResult);
  const [contextResult, ordersResult] = await Promise.allSettled([
    getCustomerContext(customerId, reason),
    listCustomerOrders(customerId),
  ]);
  const context =
    contextResult.status === 'fulfilled'
      ? customerContextFromEnvelope(contextResult.value)
      : undefined;
  if (contextResult.status === 'rejected') failedRegions.push('cases, remedies, and wallet');
  const orders =
    ordersResult.status === 'fulfilled'
      ? ordersFromEnvelope(ordersResult.value)
      : ordersFromEnvelope({ data: context });
  if (ordersResult.status === 'rejected' && orders.length === 0) failedRegions.push('orders');
  return {
    customer,
    orders,
    cases: casesFromContext(context),
    remedies: remediesFromContext(context),
    wallet: walletFromContext(context),
    flags: flagsFromCustomer(customer, context),
    failedRegions,
  };
}

export async function loadMerchant360(businessId: string): Promise<Merchant360Load> {
  const business = await getBusiness(businessId);
  const profile = businessFromEnvelope(business);
  const [contextResult, branchesResult, staffResult] = await Promise.allSettled([
    getBusinessContext(businessId),
    listBusinessBranches(businessId),
    listBusinessStaff(businessId),
  ]);
  const failedRegions: string[] = [];
  const context =
    contextResult.status === 'fulfilled'
      ? businessContextFromEnvelope(contextResult.value)
      : undefined;
  if (contextResult.status === 'rejected') {
    failedRegions.push('performance, deductions, and disputes');
  }
  const branches =
    branchesResult.status === 'fulfilled'
      ? branchesFromEnvelope(branchesResult.value)
      : branchesFromEnvelope({ data: context });
  if (branchesResult.status === 'rejected' && branches.length === 0) failedRegions.push('branches');
  const staff = staffResult.status === 'fulfilled' ? staffFromEnvelope(staffResult.value) : [];
  return {
    business: profile,
    branches,
    staff,
    performance: merchantPerformanceFrom(profile, context),
    deductions: merchantDeductionsFrom(context),
    disputes: merchantDisputesFrom(profile, context),
    verification: merchantVerificationFrom(profile, context),
    failedRegions,
  };
}

export async function loadSupportMirror(
  subjectKind: MirrorSubjectKind,
  subjectId: string,
  accessReason?: string,
) {
  if (subjectKind === 'customer') {
    const context = await getCustomerContext(subjectId, accessReason?.trim() || 'Support mirror');
    const payload = customerContextFromEnvelope(context);
    return {
      subjectKind,
      subjectId,
      context: payload,
      panels: mirrorPanelsFromContext(payload),
    };
  }
  const context = await getBusinessContext(subjectId);
  const payload = businessContextFromEnvelope(context);
  return {
    subjectKind,
    subjectId,
    context: payload,
    panels: mirrorPanelsFromContext(payload),
  };
}

export async function startImpersonationSession(body: {
  subjectAccountId: string;
  supportCaseId: string;
  justification: string;
  consentEvidence?: string;
  emergencyBasis?: string;
  mode?: 'READ_ONLY' | 'READ_WRITE';
  durationMinutes?: number;
}) {
  const client = getAdminClient();
  const { data, error, response } = await client.POST('/admin/v1/impersonation/sessions', {
    body: body as never,
  });
  if (!response.ok || error) throwSdkError(error);
  return data;
}

export async function endImpersonationSession(sessionId: string) {
  const client = getAdminClient();
  const { data, error, response } = await client.DELETE('/admin/v1/impersonation/sessions/{id}', {
    params: { path: { id: sessionId } },
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}
